import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../contexts/ProjectContext';
import { TaskStatus, TaskStatusLabels, TaskStatusColors } from '../types';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WEEKDAY_LABELS } from '../constants';
import { useCalendarWeek } from '../hooks';
import { getAllSubElements, getUserName } from '../data/mockData';
import { CalendarIcon } from '../components/icons/Icons';
import { GanttChart } from '../components/charts/GanttChart';
import { buildGanttRows } from '../utils/ganttHelpers';
import { AppSectionHero, AppStatCard, AppToolbar } from '../components/common/AppSurface';
import './CalendarPage.css';

const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const CalendarPage: React.FC = () => {
  const { activeProject } = useProjects();
  const navigate = useNavigate();

  const { weekDays, prevWeek, nextWeek, goToToday, weekLabel } = useCalendarWeek();
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'ALL'>('ALL');
  const [viewMode, setViewMode] = useState<'calendar' | 'gantt'>('calendar');

  const allTasks = useMemo(() => {
    if (!activeProject) return [];
    return getAllSubElements(activeProject);
  }, [activeProject]);

  const filteredTasks = useMemo(() => {
    let tasks = allTasks.filter(t => t.startDate && t.endDate);
    if (statusFilter !== 'ALL') {
      tasks = tasks.filter(t => t.status === statusFilter);
    }
    return tasks;
  }, [allTasks, statusFilter]);

  // Build hierarchical rows for the Gantt chart
  const ganttRows = useMemo(() => {
    if (!activeProject) return [];
    return buildGanttRows(activeProject);
  }, [activeProject]);

  // Weekly data — tasks mapped to each day of the week
  const weekData = useMemo(() => {
    const tasksByDate = new Map<string, typeof filteredTasks>();
    filteredTasks.forEach(task => {
      if (!task.endDate) return;
      const start = task.startDate ? new Date(task.startDate + 'T00:00:00') : new Date(task.endDate + 'T00:00:00');
      const end = new Date(task.endDate + 'T00:00:00');
      const cur = new Date(start);
      while (cur <= end) {
        const key = cur.toISOString().split('T')[0];
        if (!tasksByDate.has(key)) tasksByDate.set(key, []);
        tasksByDate.get(key)!.push(task);
        cur.setDate(cur.getDate() + 1);
      }
    });
    return tasksByDate;
  }, [filteredTasks]);

  const handleTaskClick = (taskId: string) => {
    navigate(`/calendar/${taskId}`);
  };

  if (!activeProject) {
    return (
      <div className="calendar-page-container">
        <AppSectionHero
          icon={<CalendarIcon size={28} />}
          title="Calendar"
          subtitle="No active project."
        />
        <p className="text-muted-center">No active project.</p>
      </div>
    );
  }

  const todayStr = new Date().toISOString().split('T')[0];

  return (
    <div className="calendar-page-container">
      <AppSectionHero
        icon={<CalendarIcon size={28} />}
        title="Project Calendar"
        subtitle={<>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></>}
        aside={
          <>
            <AppStatCard icon={<CalendarIcon size={20} />} label="Scheduled" value={filteredTasks.length} caption="Tasks on the timeline" tone="blue" />
            <AppStatCard icon={<CalendarIcon size={20} />} label="View" value={viewMode === 'calendar' ? 'Week' : 'Gantt'} caption="Current layout" tone="mint" />
          </>
        }
      />

      {/* Controls */}
      <AppToolbar
        className="calendar-page-controls"
        left={
          <div className="app-surface-chip-group calendar-page-view-toggle">
          <button
            className={`app-surface-chip-btn ${viewMode === 'calendar' ? 'active' : ''}`}
            onClick={() => setViewMode('calendar')}
          >
            Calendar
          </button>
          <button
            className={`app-surface-chip-btn ${viewMode === 'gantt' ? 'active' : ''}`}
            onClick={() => setViewMode('gantt')}
          >
            Gantt Chart
          </button>
          </div>
        }
        right={
          <>
            {viewMode === 'calendar' && (
              <select
                className="app-surface-select cal-page-filter"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as TaskStatus | 'ALL')}
              >
                <option value="ALL">All Statuses</option>
                {(Object.keys(TaskStatusLabels) as TaskStatus[]).map(s => (
                  <option key={s} value={s}>{TaskStatusLabels[s]}</option>
                ))}
              </select>
            )}
            <span className="cal-page-task-count">{filteredTasks.length} tasks</span>
          </>
        }
      />

      {/* Calendar View — Weekly Columns */}
      {viewMode === 'calendar' && (
        <div className="calendar-page-calendar">
          <div className="calendar-page-nav">
            <button className="cal-page-nav-btn" onClick={prevWeek}>‹</button>
            <button className="cal-page-today-btn" onClick={goToToday}>Today</button>
            <span className="cal-page-month-label">{weekLabel}</span>
            <button className="cal-page-nav-btn" onClick={nextWeek}>›</button>
          </div>

          <div className="cal-week-columns">
            {weekDays.map((day, i) => {
              const dayStr = day.toISOString().split('T')[0];
              const isToday = dayStr === todayStr;
              const dayTasks = weekData.get(dayStr) || [];

              return (
                <div
                  key={i}
                  className={`cal-week-col ${isToday ? 'today' : ''}`}
                >
                  <div className={`cal-week-col-header ${isToday ? 'today' : ''}`}>
                    <span className="cal-week-col-dayname">{WEEKDAY_FULL[i]}</span>
                    <span className={`cal-week-col-date ${isToday ? 'today' : ''}`}>
                      {day.getDate()}
                    </span>
                    {dayTasks.length > 0 && (
                      <span className="cal-week-col-count">{dayTasks.length}</span>
                    )}
                  </div>
                  <div className="cal-week-col-body">
                    {dayTasks.map(task => (
                      <div
                        key={task.id}
                        className="cal-week-task-card"
                        onClick={() => handleTaskClick(task.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleTaskClick(task.id);
                          }
                        }}
                        title={`${task.code} — ${task.name}\nAssigned: ${task.assignedTo.map(getUserName).join(', ')}`}
                      >
                        <div
                          className="cal-week-task-status-bar"
                          style={{ backgroundColor: TaskStatusColors[task.status] }}
                        />
                        <div className="cal-week-task-content">
                          <span className="cal-week-task-code">{task.code}</span>
                          <span className="cal-week-task-name">{task.name}</span>
                          <span className="cal-week-task-assignees">
                            {task.assignedTo.map(getUserName).join(', ')}
                          </span>
                        </div>
                      </div>
                    ))}
                    {dayTasks.length === 0 && (
                      <span className="cal-week-empty">No tasks</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Gantt View */}
      {viewMode === 'gantt' && (
        <div className="calendar-page-gantt">
          <GanttChart
            rows={ganttRows}
            projectStartDate={activeProject.startDate}
            projectEndDate={activeProject.endDate}
            onTaskClick={handleTaskClick}
            maxRows={Infinity}
          />
        </div>
      )}
    </div>
  );
};
